/**
 * Skill Matching Utilities
 *
 * Pure utility functions for checking skill requirements.
 * These utilities have no dependencies and work in both frontend and backend.
 *
 * Note: Uses Set for O(1) lookups. Callers should pre-build Sets for repeated checks.
 */

import { normaliseSkillEntry, type ShiftSkillEntry } from '../models/shift-rules.model';

/** skillId -> effective rating (`null` = held but unrated). Built ONLY via buildEffectiveSkillRatings(). */
export type EffectiveSkillRatings = ReadonlyMap<string, number | null>;

/** One person_skills row as loaded by any consumer (backend Drizzle rows or client DTOs). */
export interface PersonSkillRow {
  skillId: string;
  rating?: number | null;
  expiryDate?: Date | string | null;
}

/** Per-skill failure detail for UX reasons / solver diagnostics. */
export interface UnmetSkillRequirement {
  skillId: string;
  minRating: number;
  /** Effective rating the person has (`null` = held but unrated). Only meaningful when `held`. */
  actualRating: number | null;
  /** false = skill missing entirely; true = held but below the threshold. */
  held: boolean;
}

/** Tolerance for float4 round-trips of normalized ratings (e.g. 0.6 stored as 0.60000002384). */
export const SKILL_RATING_EPSILON = 1e-6;

const toDateKey = (value: Date | string): string => (typeof value === 'string' ? value.slice(0, 10) : value.toISOString().slice(0, 10));

/**
 * Collapse person_skills rows (duplicates allowed for recertification) to one
 * effective rating per skill.
 *
 * Rating is the max among rows that are still valid at `atDate` — a lapsed
 * certification must not lend its rating to the holder. Recertifying downwards
 * (old row 0.9 expired, new row 0.5 valid) therefore yields 0.5, not 0.9.
 * When every row for a skill has lapsed, the max across all rows is used and
 * the skill stays in the map: presence is decided here, currency is decided
 * per-date by the caller's expiry filter, which is the only thing that can see
 * whether a mid-window lapse affects a given shift.
 *
 * `atDate` defaults to today. Callers solving a window pass nothing — their
 * per-slot filter handles dates — but the default still keeps definitively
 * stale certifications from inflating the number.
 */
export function buildEffectiveSkillRatings(rows: readonly PersonSkillRow[], atDate: Date = new Date()): EffectiveSkillRatings {
  const atKey = toDateKey(atDate);
  const valid = new Map<string, number | null>();
  const anyRow = new Map<string, number | null>();

  const merge = (target: Map<string, number | null>, skillId: string, rating: number | null) => {
    const existing = target.get(skillId);
    if (existing === undefined || (rating !== null && (existing === null || rating > existing))) {
      target.set(skillId, rating);
    }
  };

  for (const row of rows) {
    const rating = row.rating ?? null;
    merge(anyRow, row.skillId, rating);
    if (row.expiryDate && toDateKey(row.expiryDate) < atKey) continue;
    merge(valid, row.skillId, rating);
  }

  // Fall back per skill, not wholesale: one lapsed skill must not drag down the
  // ratings of the person's other, still-valid skills.
  for (const [skillId, rating] of anyRow) {
    if (!valid.has(skillId)) valid.set(skillId, rating);
  }

  return valid;
}

function meetsRequirement(skills: EffectiveSkillRatings, requirement: { skillId: string; minRating: number }): boolean {
  if (!skills.has(requirement.skillId)) return false;
  return (skills.get(requirement.skillId) ?? 0) + SKILL_RATING_EPSILON >= requirement.minRating;
}

/** ALL requirements met: skill held AND (rating ?? 0) meets the threshold (with float tolerance). */
export function isQualifiedForAll(skills: EffectiveSkillRatings, requirements: readonly ShiftSkillEntry[]): boolean {
  return requirements.every(entry => meetsRequirement(skills, normaliseSkillEntry(entry)));
}

/** Per-skill failure detail for every requirement not met (empty array = fully qualified). */
export function findUnmetSkillRequirements(
  skills: EffectiveSkillRatings,
  requirements: readonly ShiftSkillEntry[]
): UnmetSkillRequirement[] {
  const unmet: UnmetSkillRequirement[] = [];

  for (const entry of requirements) {
    const requirement = normaliseSkillEntry(entry);
    if (meetsRequirement(skills, requirement)) continue;
    const held = skills.has(requirement.skillId);
    unmet.push({
      skillId: requirement.skillId,
      minRating: requirement.minRating,
      actualRating: held ? (skills.get(requirement.skillId) ?? null) : null,
      held,
    });
  }

  return unmet;
}
